// videoServiceClient — thin helper around the Pimcore-side `/api/video-access-token`
// endpoint. The main video player path doesn't strictly need it (the server-side
// VideoPlaybackUrlResolver issues a 302 directly from the AssetHelper response
// when the user has the `video-service-playback` flag on), but custom embeds
// + future React players can pre-fetch the URL so they don't depend on the
// browser following the redirect chain.
//
// Returns null when the user doesn't have the flag enabled — caller falls
// back to whatever legacy URL they had.
/**
 * Request a short-lived JWT + signed playback URL for one video. Returns null
 * when the endpoint reports 404 (flag off for the current user) or any other
 * non-200 result.
 *
 * The endpoint is gated server-side by `video-service-playback` — feature flag
 * off → 404 (not 401) so we never reveal whether the user has the flag through
 * status-code probing.
 */
export async function fetchVideoAccessToken(videoId, scope = 'playback') {
    try {
        const resp = await fetch('/api/video-access-token', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ videoId, scope }),
        });
        if (!resp.ok) {
            return null;
        }
        const j = await resp.json();
        if (!j.videoUrl || !j.token) {
            return null;
        }
        return j;
    }
    catch (err) {
        console.warn('videoServiceClient: token fetch failed', err);
        return null;
    }
}
/**
 * Refresh-on-401 wrapper around a player's manifest URL. The player calls
 * `resolve()` once on mount; if mid-playback hls.js reports an HTTP 401 on a
 * segment fetch, the player calls `refresh()` to mint a fresh JWT + reload
 * the manifest.
 *
 * Pattern usage with vidstack/hls.js:
 *
 *   const session = createPlayerSession(videoId, fallbackUrl);
 *   const initialSrc = await session.resolve();
 *   // ... mount player ...
 *   player.on('hls-fragment-loading-error', () => session.refresh().then(...));
 */
export function createPlayerSession(videoId, fallbackUrl) {
    let cached = null;
    async function resolve() {
        cached = await fetchVideoAccessToken(videoId, 'playback');
        if (cached) {
            return cached.videoUrl;
        }
        return fallbackUrl;
    }
    async function refresh() {
        cached = await fetchVideoAccessToken(videoId, 'playback');
        return cached ? cached.videoUrl : fallbackUrl;
    }
    return {
        resolve,
        refresh,
        getCached: () => cached,
    };
}
